import { useEffect, useState } from 'react';
import { ArrowLeftRight, Send, Share2, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';

import { useToast } from '../../components/Toast';
import { cn } from '../../lib/cn';
import { supabase } from '../../lib/supabase';
import type { CustomerOption, LeadOption, QuoteRow } from './quote.schema';
import { QUOTE_STATUS_LABELS_HE, formatILS, statusBadgeClass } from './quote.schema';
import { QuoteShareSheet } from './QuoteShareSheet';
import {
  useAcceptQuoteToProject,
  useQuoteItems,
  useSoftDeleteQuote,
  useUpdateQuoteStatus,
} from './quotesQueries';
import styles from './quotes.module.css';

interface Props {
  open: boolean;
  quote: QuoteRow | null;
  orgId: string | null;
  userId: string | null;
  customers: CustomerOption[];
  leads: LeadOption[];
  onClose: () => void;
}

export function QuoteDrawer({ open, quote, orgId, userId, customers, leads, onClose }: Props) {
  const toast = useToast();
  const quoteId = quote?.id ?? null;
  const { data: items = [] } = useQuoteItems(orgId, quoteId);
  const updateStatus = useUpdateQuoteStatus(orgId);
  const softDelete = useSoftDeleteQuote(orgId);
  const acceptToProject = useAcceptQuoteToProject(orgId, userId);

  const [shareOpen, setShareOpen] = useState(false);
  const [contactPhone, setContactPhone] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    async function loadContact() {
      if (!quote || !orgId || !quote.primary_contact_id) {
        setContactPhone(null);
        setContactEmail(null);
        return;
      }
      const { data } = await supabase
        .from('contacts')
        .select('phone, email')
        .eq('id', quote.primary_contact_id)
        .eq('organization_id', orgId)
        .maybeSingle();
      if (!active) return;
      setContactPhone(data?.phone ?? null);
      setContactEmail(data?.email ?? null);
    }
    void loadContact();
    return () => {
      active = false;
    };
  }, [quote, orgId]);

  if (!open || !quote) return null;

  const customerName = quote.customer_id
    ? customers.find((c) => c.id === quote.customer_id)?.name ?? null
    : null;
  const leadName = quote.source_lead_id
    ? leads.find((l) => l.id === quote.source_lead_id)?.full_name ?? null
    : null;

  async function handleSend() {
    if (!quote) return;
    try {
      await updateStatus.mutateAsync({ quoteId: quote.id, status: 'sent' });
      toast.success('ההצעה נשלחה');
      setShareOpen(true);
    } catch {
      toast.error('לא הצלחנו לעדכן · נסה שוב');
    }
  }

  async function handleDelete() {
    if (!quote) return;
    if (!window.confirm('למחוק את ההצעה?')) return;
    try {
      await softDelete.mutateAsync(quote.id);
      toast.success('ההצעה נמחקה');
      onClose();
    } catch {
      toast.error('לא הצלחנו למחוק · נסה שוב');
    }
  }

  async function handleConvert() {
    if (!quote) return;
    const fallbackName = customers.find((c) => c.id === quote.customer_id)?.name
      ?? leads.find((l) => l.id === quote.source_lead_id)?.full_name
      ?? quote.quote_number;
    const projectName = window.prompt('שם הפרויקט:', `פרויקט · ${fallbackName}`);
    if (!projectName?.trim()) return;
    try {
      await acceptToProject.mutateAsync({ quoteId: quote.id, projectName });
      toast.success('הלקוח והפרויקט נוצרו · ההצעה הומרה');
      onClose();
    } catch {
      toast.error('לא הצלחנו להמיר · נסה שוב');
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-end',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className={styles.section}
        style={{
          width: 'min(560px, 100%)',
          maxHeight: '100vh',
          overflowY: 'auto',
          margin: 0,
          borderRadius: 0,
          borderInlineStart: '1px solid var(--color-border, #e5e7eb)',
        }}
        onClick={(e) => { e.stopPropagation(); }}
      >
        <div className={styles.drawerHead}>
          <div>
            <h2 className={styles.drawerTitle}>
              הצעת מחיר <bdi dir="ltr">{quote.quote_number}</bdi>
            </h2>
            <span className={cn(styles.badge, styles[statusBadgeClass(quote.status)])}>
              {(QUOTE_STATUS_LABELS_HE as Record<string, string>)[quote.status] ?? quote.status}
            </span>
          </div>
          <button type="button" className={styles.btnGhost} onClick={onClose} aria-label="סגור">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className={styles.metaList}>
          <div className={styles.metaRow}>
            <span className={styles.metaKey}>לקוח</span>
            <span>{customerName ?? '—'}</span>
          </div>
          <div className={styles.metaRow}>
            <span className={styles.metaKey}>ליד מקור</span>
            <span>{leadName ?? '—'}</span>
          </div>
          <div className={styles.metaRow}>
            <span className={styles.metaKey}>תוקף עד</span>
            <span>{quote.valid_until ? format(new Date(quote.valid_until), 'dd/MM/yyyy') : '—'}</span>
          </div>
          <div className={styles.metaRow}>
            <span className={styles.metaKey}>נשלחה</span>
            <span>{quote.sent_at ? format(new Date(quote.sent_at), 'dd/MM/yyyy HH:mm') : '—'}</span>
          </div>
          <div className={styles.metaRow}>
            <span className={styles.metaKey}>נצפתה</span>
            <span>{quote.viewed_at ? format(new Date(quote.viewed_at), 'dd/MM/yyyy HH:mm') : '—'}</span>
          </div>
          <div className={styles.metaRow}>
            <span className={styles.metaKey}>אושרה</span>
            <span>{quote.accepted_at ? format(new Date(quote.accepted_at), 'dd/MM/yyyy HH:mm') : '—'}</span>
          </div>
        </div>

        <div>
          <h3 className={styles.sectionTitle} style={{ marginBlockEnd: 8 }}>שורות</h3>
          <div className={styles.itemsReadonly}>
            <div className={styles.itemRowHead}>
              <span>שירות</span>
              <span>כמות</span>
              <span>מחיר יחידה</span>
              <span>סכום</span>
            </div>
            {items.map((item) => (
              <div key={item.id} className={styles.itemReadonly}>
                <span className={styles.itemLabel}>{item.service_label}</span>
                <span><bdi>{item.quantity}</bdi></span>
                <span><bdi>{formatILS(item.unit_price)}</bdi></span>
                <span className={styles.subtotal}><bdi>{formatILS(item.subtotal ?? item.quantity * item.unit_price)}</bdi></span>
                {item.description ? (
                  <span className={styles.itemDescription}>{item.description}</span>
                ) : null}
              </div>
            ))}
            {items.length === 0 ? <p className={styles.notice}>אין שורות.</p> : null}
          </div>

          <div className={styles.totalRow}>
            <span>סה"כ:</span>
            <span className={styles.totalAmount}><bdi>{formatILS(quote.value_amount)}</bdi></span>
          </div>
        </div>

        {quote.notes ? (
          <div>
            <h3 className={styles.sectionTitle} style={{ marginBlockEnd: 8 }}>הערות</h3>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{quote.notes}</p>
          </div>
        ) : null}

        <div className={styles.actions}>
          {quote.status === 'accepted' ? (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => { void handleConvert(); }}
              disabled={acceptToProject.isPending}
            >
              <ArrowLeftRight size={16} aria-hidden="true" />
              המר ללקוח + פרויקט
            </button>
          ) : null}
          {quote.status === 'draft' ? (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => { void handleSend(); }}
              disabled={updateStatus.isPending}
            >
              <Send size={16} aria-hidden="true" />
              שלח ושתף
            </button>
          ) : null}
          {quote.status !== 'draft' && quote.status !== 'accepted' ? (
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => { setShareOpen(true); }}
            >
              <Share2 size={16} aria-hidden="true" />
              שתף
            </button>
          ) : null}
          <button
            type="button"
            className={styles.btnDanger}
            onClick={() => { void handleDelete(); }}
            disabled={softDelete.isPending}
          >
            <Trash2 size={14} aria-hidden="true" />
            מחק
          </button>
        </div>
      </div>

      <QuoteShareSheet
        open={shareOpen}
        quoteId={quote.id}
        quoteNumber={quote.quote_number}
        customerName={customerName}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        onClose={() => { setShareOpen(false); }}
      />
    </div>
  );
}
